import type { FC, PropsWithChildren } from 'react';
import React, { useContext, useMemo } from 'react';

import type { UIFeatures } from '../../types';
import UIFeaturesContext from '../context';

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export interface UIFeaturesProviderProps {
  readonly features: UIFeatures;
}

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const UIFeaturesProvider: FC<PropsWithChildren<UIFeaturesProviderProps>> = ({
  features: overridenFeatures,
  ...props
}) => {
  const inheritedFeatures = useContext(UIFeaturesContext);

  const mergedFeatures = useMemo<UIFeatures>(() => {
    const features = { ...inheritedFeatures, ...overridenFeatures };

    if (process.env.NODE_ENV === 'production') {
      return features;
    }

    // When used on objects, the spread syntax enumerates and adds the key-value pairs to a new object. This means that any previous Proxy instance will be lost after the new object creation.
    // To circumvent this behaviour, we create a new Proxy reference that will delegate each get property operation to its original features object.
    // This is necessary to enable `useStrictFeatureSettings` uninitialized feature detection in development mode.
    return new Proxy(features, {
      get: (_target, prop, receiver) => {
        const target =
          !inheritedFeatures || Object.prototype.hasOwnProperty.call(overridenFeatures, prop)
            ? overridenFeatures
            : inheritedFeatures;

        return Reflect.get(target, prop, receiver);
      },
    });
  }, [inheritedFeatures, overridenFeatures]);

  return <UIFeaturesContext.Provider {...props} value={mergedFeatures} />;
};
